import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/siteData';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import NotFound from '@/pages/NotFound';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = categories.find((c) => c.slug === slug);
  if (!category) return <NotFound />;

  const categoryArticles = articles.filter((a) => a.category === slug);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${category.name} - 白丝JK正版周边购买指南`,
    description: category.description,
    url: `https://shiroi-jk.com/category/${slug}`,
    inLanguage: 'zh-CN',
    isPartOf: {
      '@type': 'WebSite',
      name: '白丝JK正版周边购买指南',
      url: 'https://shiroi-jk.com',
    },
  };

  const categoryNames: Record<string, string> = {
    shouban: '手办周边',
    fuzhuang: 'JK服装',
    tongren: '同人文化',
    gouwu: '购物指南',
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <SEOHead
        title={category.name}
        description={category.description}
        keywords={`白丝jk,白丝jk${category.name},jk${category.name}`}
        canonicalUrl={`https://shiroi-jk.com/category/${slug}`}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: category.name },
              ]}
            />
            <div className="mt-6 flex flex-col lg:flex-row items-start lg:items-center gap-6">
              <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-primary-500 text-background-50 flex-shrink-0">
                <i className={`${category.icon} text-2xl`}></i>
              </div>
              <div>
                <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-2">
                  {category.name}
                </h1>
                <p className="text-foreground-600 text-sm max-w-2xl">{category.description}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 py-10">
          <div className="flex flex-wrap items-center gap-2 mb-8">
            <span className="text-sm text-foreground-500">分类筛选：</span>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {categoryArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-50 rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer transform hover:-translate-y-1 border border-background-200/70"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={article.coverImage}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="px-2.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                        {categoryNames[article.category] || article.category}
                      </span>
                      <span className="text-xs text-foreground-400">{article.date}</span>
                    </div>
                    <h2 className="font-heading text-base text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                      {article.title}
                    </h2>
                    <p className="text-sm text-foreground-500 line-clamp-2 leading-relaxed">
                      {article.summary}
                    </p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-3">
                      {article.tags.map((tag) => (
                        <span key={tag} className="text-xs text-foreground-400 bg-background-100 px-2 py-0.5 rounded">
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400">
                <i className="ri-file-list-3-line text-2xl"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类暂无文章</p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}